import { Link } from 'react-router-dom';
import { DeliveryDining, Place } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { TrackingIconSlot } from '../models/TrackingIcon';
import {
  TrackingIconScheduleSegment,
  calendarDays,
  monthBounds,
} from '../utils/trackingIconSchedule';
import TrackingIconImage from './TrackingIconImage';

export interface TrackingScheduleLane {
  slot: TrackingIconSlot;
  segments: TrackingIconScheduleSegment[];
}

interface TrackingIconScheduleViewProps {
  month: Date;
  lanes: TrackingScheduleLane[];
  view: 'timeline' | 'calendar';
  selectedDay: Date;
  onSelectDay: (day: Date) => void;
}

const dayFormat = new Intl.DateTimeFormat('es-UY', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const instantFormat = new Intl.DateTimeFormat('es-UY', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});
const slotName = (slot: TrackingIconSlot) =>
  slot === 'driver' ? 'Repartidor' : 'Destino';
const segmentName = ({ icon }: TrackingIconScheduleSegment) =>
  icon?.name ?? 'Sin predeterminado';
const segmentType = ({ icon }: TrackingIconScheduleSegment) =>
  !icon ? 'Sin predeterminado' : icon.isDefault ? 'Predeterminado' : 'Especial';
const segmentColors = ({ icon }: TrackingIconScheduleSegment) =>
  !icon
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : icon.isDefault
      ? 'border-gray-300 bg-gray-100 text-gray-700'
      : 'border-emerald-300 bg-emerald-50 text-emerald-900';
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const daySegments = (segments: TrackingIconScheduleSegment[], day: Date) => {
  const end = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
  return segments
    .filter((segment) => segment.from < end && segment.to > day)
    .map((segment) => ({
      ...segment,
      from: new Date(Math.max(+segment.from, +day)),
      to: new Date(Math.min(+segment.to, +end)),
    }));
};

const SlotHeading = ({ slot }: { slot: TrackingIconSlot }) => (
  <span className='flex items-center gap-2'>
    {slot === 'driver' ? (
      <DeliveryDining fontSize='small' />
    ) : (
      <Place fontSize='small' />
    )}
    {slotName(slot)}
  </span>
);

const SegmentCard = ({
  segment,
  slot,
  id,
}: {
  segment: TrackingIconScheduleSegment;
  slot: TrackingIconSlot;
  id?: string;
}) => (
  <li
    id={id}
    className='flex scroll-mt-6 flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-white p-4'
  >
    {segment.icon && (
      <TrackingIconImage
        imageUrl={segment.icon.imageUrl}
        name={segment.icon.name}
        size={52}
      />
    )}
    <div className='min-w-0 flex-1 basis-56'>
      <div className='flex flex-wrap items-center gap-2'>
        <p className='font-medium text-gray-900 [overflow-wrap:anywhere]'>
          {segmentName(segment)}
        </p>
        {segment.icon && (
          <span
            className={`rounded border px-2 py-0.5 text-xs ${segmentColors(segment)}`}
          >
            {segmentType(segment)}
          </span>
        )}
      </div>
      <p className='mt-1 text-sm text-gray-600'>
        Desde {instantFormat.format(segment.from)} hasta{' '}
        {instantFormat.format(segment.to)}
      </p>
      {!segment.icon && (
        <p className='mt-1 text-sm text-amber-800'>
          No hay un ícono configurado para este período. Se usará la apariencia
          habitual del seguimiento.
        </p>
      )}
    </div>
    {segment.icon ? (
      <Link
        className='text-sm font-medium text-[var(--primary-color)] underline underline-offset-4'
        to={`/institutional-web/live-tracking/${slot}-icon/${segment.icon.id}`}
      >
        Ver ícono
      </Link>
    ) : (
      <Link
        className='text-sm font-medium text-[var(--primary-color)] underline underline-offset-4'
        to={`/institutional-web/live-tracking/${slot}-icon`}
      >
        Configurar íconos
      </Link>
    )}
  </li>
);

const TrackingIconScheduleView = ({
  month,
  lanes,
  view,
  selectedDay,
  onSelectDay,
}: TrackingIconScheduleViewProps) => {
  const { from, to } = monthBounds(month);
  const days = calendarDays(month);
  const today = new Date();

  if (view === 'timeline') {
    return (
      <div className='flex flex-col gap-6'>
        {lanes.map(({ slot, segments }) => (
          <section
            key={slot}
            aria-label={`Línea de tiempo de ${slotName(slot).toLowerCase()}`}
            className='rounded-lg border border-gray-200 bg-white p-4 sm:p-6'
          >
            <h2 className='mb-5 text-lg font-medium'>
              <SlotHeading slot={slot} />
            </h2>
            <div
              className='relative mb-2 h-4 text-xs text-gray-500'
              aria-hidden='true'
            >
              {[1, 8, 15, 22, new Date(+to - 1).getDate()].map((day) => (
                <span
                  key={day}
                  className='absolute'
                  style={{
                    left: `${((+new Date(month.getFullYear(), month.getMonth(), day) - +from) / (+to - +from)) * 100}%`,
                    transform: day === 1 ? undefined : 'translateX(-50%)',
                  }}
                >
                  {day}
                </span>
              ))}
            </div>
            <div
              className='relative mb-5 h-14 overflow-hidden rounded-lg border border-gray-200'
              aria-label='Distribución de íconos durante el mes'
            >
              {segments.map((segment, index) => (
                <Tooltip
                  key={+segment.from}
                  title={`${segmentName(segment)} · ${instantFormat.format(segment.from)} → ${instantFormat.format(segment.to)}`}
                  arrow
                >
                  <a
                    href={`#schedule-${slot}-${index}`}
                    style={{
                      left: `${((+segment.from - +from) / (+to - +from)) * 100}%`,
                      width: `${((+segment.to - +segment.from) / (+to - +from)) * 100}%`,
                      boxShadow: 'inset -1px 0 0 rgba(107, 114, 128, 0.25)',
                    }}
                    className={`absolute inset-y-0 flex min-w-0 items-center justify-center overflow-hidden text-xs font-medium focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] ${segmentColors(segment)}`}
                    aria-label={`Ver período de ${segmentName(segment)} desde ${instantFormat.format(segment.from)}`}
                  >
                    <span className='truncate px-1'>
                      {segmentName(segment)}
                    </span>
                  </a>
                </Tooltip>
              ))}
            </div>
            <ol className='flex flex-col gap-3'>
              {segments.map((segment, index) => (
                <SegmentCard
                  key={+segment.from}
                  id={`schedule-${slot}-${index}`}
                  segment={segment}
                  slot={slot}
                />
              ))}
            </ol>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      <section
        aria-label='Calendario de íconos'
        className='overflow-hidden rounded-lg border border-gray-200 bg-white'
      >
        <p className='border-b border-gray-200 px-4 py-3 text-sm text-gray-600'>
          Seleccioná un día para ver sus íconos y las horas de cada cambio.
        </p>
        <div className='overflow-x-auto'>
          <div className='min-w-[700px]'>
            <div className='grid grid-cols-7 bg-gray-50 text-center text-xs font-medium text-gray-600'>
              {[
                'Lunes',
                'Martes',
                'Miércoles',
                'Jueves',
                'Viernes',
                'Sábado',
                'Domingo',
              ].map((day) => (
                <div key={day} className='py-3'>
                  {day}
                </div>
              ))}
            </div>
            <div className='grid grid-cols-7'>
              {days.map((day) => {
                const outside = day.getMonth() !== month.getMonth();
                const selected = sameDay(day, selectedDay);
                return (
                  <button
                    key={+day}
                    type='button'
                    disabled={outside}
                    aria-pressed={selected && !outside}
                    aria-current={sameDay(day, today) ? 'date' : undefined}
                    aria-label={`Ver programación del ${dayFormat.format(day)}`}
                    onClick={() => onSelectDay(day)}
                    className={`min-h-36 min-w-0 border-t border-r border-gray-200 p-2 text-left align-top transition-colors last:border-r-0 focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--primary-color)] ${outside ? 'bg-gray-50 text-gray-400' : selected ? 'bg-orange-50 ring-2 ring-inset ring-[var(--primary-color)]' : 'hover:bg-gray-50'}`}
                  >
                    <span
                      className={`mb-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${sameDay(day, today) ? 'bg-[var(--primary-color)] text-white' : ''}`}
                    >
                      {day.getDate()}
                    </span>
                    {!outside && (
                      <span className='flex flex-col gap-2'>
                        {lanes.map(({ slot, segments }) => {
                          const items = daySegments(segments, day);
                          const unique = items.filter(
                            (item, index) =>
                              items.findIndex(
                                (other) => other.icon?.id === item.icon?.id
                              ) === index
                          );
                          return (
                            <span key={slot} className='block min-w-0'>
                              <span className='mb-1 block text-[10px] font-medium text-gray-500'>
                                {slotName(slot)}
                              </span>
                              {unique.slice(0, 2).map((segment) => (
                                <span
                                  key={segment.icon?.id ?? 'none'}
                                  title={`${segmentName(segment)} · ${segmentType(segment)}`}
                                  className={`mb-1 flex items-center gap-1 rounded border px-1 py-0.5 text-[11px] ${segmentColors(segment)}`}
                                >
                                  {segment.icon?.imageUrl && (
                                    <img
                                      key={segment.icon.imageUrl}
                                      src={segment.icon.imageUrl}
                                      alt=''
                                      loading='lazy'
                                      className='h-4 w-4 shrink-0 object-contain'
                                      onError={(event) => {
                                        event.currentTarget.style.visibility =
                                          'hidden';
                                      }}
                                      onLoad={(event) => {
                                        event.currentTarget.style.visibility =
                                          'visible';
                                      }}
                                    />
                                  )}
                                  <span className='truncate'>
                                    {segmentName(segment)}
                                  </span>
                                </span>
                              ))}
                              {unique.length > 2 && (
                                <span className='block text-[10px] text-gray-600'>
                                  +{unique.length - 2} más
                                </span>
                              )}
                              {items.length > 1 && (
                                <span className='block text-[10px] text-gray-600'>
                                  {items.length - 1}{' '}
                                  {items.length === 2 ? 'cambio' : 'cambios'}
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <section
        aria-live='polite'
        aria-label='Detalle del día seleccionado'
        className='rounded-lg border border-gray-200 bg-white p-4 sm:p-6'
      >
        <h2 className='mb-5 text-lg font-medium'>
          {dayFormat.format(selectedDay)}
        </h2>
        <div className='flex flex-col gap-5'>
          {lanes.map(({ slot, segments }) => (
            <div key={slot}>
              <h3 className='mb-3 font-medium'>
                <SlotHeading slot={slot} />
              </h3>
              <ol className='flex flex-col gap-3'>
                {daySegments(segments, selectedDay).map((segment) => (
                  <SegmentCard
                    key={+segment.from}
                    segment={segment}
                    slot={slot}
                  />
                ))}
              </ol>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default TrackingIconScheduleView;
